import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useGetSchoolClassQuery } from '../../store/api/schoolAdminApi'
import { useSchoolFilters } from '../../components/school/SchoolLayout'
import DateRangeBar from '../../components/school/DateRangeBar'
import { KpiCard, formatDate } from '../../components/school/SchoolUi'

const SchoolClassDetail: React.FC = () => {
  const { t } = useTranslation('school')
  const { projectId } = useParams()
  const { filters } = useSchoolFilters()
  const numericProjectId = Number(projectId)
  const { data, isLoading } = useGetSchoolClassQuery(
    { ...filters, projectId: numericProjectId },
    { skip: !Number.isFinite(numericProjectId) || numericProjectId <= 0 },
  )

  if (isLoading) return <div className="text-gray-500">{t('common.loading')}</div>
  if (!data) return <div className="text-gray-500">{t('classes.empty')}</div>

  const c = data.class
  return (
    <div className="space-y-6">
      <div>
        <Link to="/pending/classes" className="text-sm text-blue-600 hover:underline">{t('classes.back')}</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-2">{c.name}</h1>
        <p className="text-gray-600">{t('classes.detailTitle')} · {c.teacherName || t('common.na')}</p>
      </div>
      <DateRangeBar showClassFilter={false} />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard label={t('classes.columns.students')} value={c.studentCount} />
        <KpiCard label={t('classes.columns.active')} value={c.activeStudents} />
        <KpiCard label={t('classes.units')} value={c.unitCount} />
        <KpiCard label={t('classes.columns.activities')} value={c.activityCount} />
        <KpiCard label={t('overview.aiSessions')} value={c.aiSessions} />
        <KpiCard label={t('overview.aiInteractions')} value={c.aiInteractions} />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b"><h2 className="font-semibold">{t('classes.units')}</h2></div>
        {!(data.units || []).length ? (
          <div className="p-6 text-gray-500">{t('classes.noUnits')}</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('classes.unitName')}</th>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('classes.activityCount')}</th>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('classes.participating')}</th>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('classes.participationRate')}</th>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('overview.aiSessions')}</th>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('classes.lastActivity')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.units.map((u: any) => (
                <tr key={u.id}>
                  <td className="px-4 py-3 text-sm">
                    <Link to={`/pending/units/${u.id}`} className="text-blue-600 hover:underline">{u.name || t('common.na')}</Link>
                  </td>
                  <td className="px-4 py-3 text-sm">{u.activityCount}</td>
                  <td className="px-4 py-3 text-sm">{u.participatingStudents}</td>
                  <td className="px-4 py-3 text-sm">{u.participationRate}%</td>
                  <td className="px-4 py-3 text-sm">{u.aiSessions}</td>
                  <td className="px-4 py-3 text-sm">{formatDate(u.lastActivity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default SchoolClassDetail
